// src/pages/Onboarding/slides/FinalConversionSlide.js

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import SlideShell from '../SlideShell';
import MetallicCard from '../../../components/Onboarding/MetallicCard';
import { ReactComponent as MatchmedicLogo } from '../../../images/matchmedic_logo.svg';
import WebGlGradientCanvas from '../../../components/Onboarding/WebGlGradientCanvas';
import lockedIcon from '../../../images/optionIcons/locked.svg';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const CARD_W = 300;
const CARD_H = 504;
const CARD_RADIUS = 28;

// Glow constants
const GLOW_SPREAD = 10; // px outside the card
const GLOW_BLUR_PX = 40; // blur radius for the glow

// Flash constants to mirror ConversionSlide final card reveal
const TARGET_CARD_BRIGHTNESS = 1.2;
const CARD_BRIGHTNESS_MULTIPLIER = 20;
const CARD_BRIGHTNESS_FADE_DURATION_MS = 2000;

const FINAL_CARD_HIGHLIGHT_WIDTH = 12;
const FINAL_CARD_SHADOW_WIDTH = 12;
const EDGE_HIGHLIGHT_MAX_OPACITY = 0.78;
const EDGE_SHADOW_MAX_OPACITY = 0.65;
const FINAL_EDGE_CORNER_SCALE = 1.15;
const FINAL_CARD_LIGHT_OVERLAY = 'radial-gradient(circle at 85% 15%, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.14) 50%, rgba(255,255,255,0) 82%)';
const FINAL_CARD_BASE_COLOR = 'rgba(128, 134, 142, 0.38)';
const FINAL_CARD_CONTENT_TOP_OFFSET = 0;
const FINAL_CARD_CONTENT_GAP = 0;
const FINAL_CARD_LOGO_WIDTH = 128;
const FINAL_CARD_LABEL_MARGIN_TOP = -20;
const FINAL_CARD_LOGO_DROP_SHADOW = 'drop-shadow(0 0 12px rgba(0,0,0,0.3))';
const FINAL_CARD_LABEL_TEXT_SHADOW = '0 0 12px rgba(0,0,0,0.3)';

// Preliminary colored-cards animation timings (ms)
const PRE_DELAY_MS = 100; // delay before merge starts
const MERGE_ANIM_MS = 800; // cards move to center
const PRE_FLASH_MS = 1000;  // brightness ramp duration before swapping to final
const FLASH_DECAY_MS = 500; // brightness ramp down after final appears

// Simple progress bar used for colored cards
function useAnimatedPercent(targetPercent, durationMs = 900, delayMs = 50) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf;
    const startAt = performance.now() + delayMs;
    const start = () => {
      const t0 = performance.now();
      const run = (ts) => {
        const p = Math.min(1, (ts - t0) / durationMs);
        const eased = 1 - Math.pow(1 - p, 3);
        setValue(targetPercent * eased);
        if (p < 1) raf = requestAnimationFrame(run);
      };
      raf = requestAnimationFrame(run);
    };
    const id = setTimeout(start, delayMs);
    return () => { clearTimeout(id); if (raf) cancelAnimationFrame(raf); };
  }, [targetPercent, durationMs, delayMs]);
  return value;
}

function ProgressBar({ percent, minLabel = 'Low', maxLabel = 'High', label, color = 'bg-white', trackColor = 'bg-white/30', heightClass = 'h-6', labelTextClass = 'text-gray-900' }) {
  const animated = useAnimatedPercent(percent, 900, 80);
  const widthStyle = { width: `${Math.max(0, Math.min(100, animated)).toFixed(1)}%` };
  return (
    <div className="w-full">
      <div className={`relative w-full ${heightClass} rounded-full ${trackColor}`}>
        <div className={`absolute inset-y-0 left-0 rounded-full ${color} flex items-center`} style={widthStyle}>
          {label && (
            <span className={`ml-2 inline-flex items-center rounded-full bg-white/90 px-2 py-0.5 text-xs font-semibold shadow-sm ${labelTextClass}`}>
              {label}
            </span>
          )}
        </div>
      </div>
      <div className="mt-2 flex w-full justify-between text-xs text-white/90">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );
}

function DoubleProgressGroup({ bars = [], minLabel = 'Low', maxLabel = 'High' }) {
  return (
    <div className="w-full">
      <div className="space-y-3">
        {bars.map((b, idx) => (
          <div key={idx} className="w-full">
            <ProgressBar percent={b.percent} label={b.label} minLabel="" maxLabel="" color={b.color} trackColor={b.trackColor} labelTextClass={b.labelTextClass || 'text-cyan-700'} />
          </div>
        ))}
      </div>
      <div className="mt-2 flex w-full justify-between text-xs text-white/90">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );
}

function ColoredCard({ title, subtitle, gradientClass, children, style, className='' }) {
  return (
    <div className={`rounded-2xl p-4 sm:p-5 shadow-lg text-white ${gradientClass} ${className}`} style={style}> 
      <div className="mb-3">
        <h3 className="text-base sm:text-lg font-semibold leading-snug">{title}</h3>
        {subtitle && <p className="mt-1 text-xs sm:text-sm text-white/90">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function createEdgeHighlightStyle({ radius, width, opacity, cornerScale = 1 }) {
  const safeWidth = Math.max(width ?? 0, 1);
  const safeRadius = Math.max(radius ?? 0, 0);
  const highlight = `rgba(255,255,255,${clamp(opacity ?? 0, 0, 1)})`;
  const transparentHighlight = 'rgba(255,255,255,0)';
  const cornerSize = Math.max(safeWidth * 2, safeRadius * cornerScale);
  const topEdge = `linear-gradient(to bottom, ${highlight}, ${transparentHighlight})`;
  const rightEdge = `linear-gradient(to left, ${highlight}, ${transparentHighlight})`;
  const topRightCorner = `radial-gradient(circle at 100% 0, ${highlight} 0%, ${transparentHighlight} 70%)`;

  return {
    borderRadius: safeRadius,
    backgroundImage: [topEdge, rightEdge, topRightCorner].join(', '),
    backgroundSize: [`100% ${safeWidth}px`, `${safeWidth}px 100%`, `${cornerSize}px ${cornerSize}px`].join(', '),
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'top center, center right, top right',
    backgroundOrigin: 'border-box',
    pointerEvents: 'none',
  };
}

function createEdgeShadowStyle({ radius, width, opacity, cornerScale = 1 }) {
  const safeWidth = Math.max(width ?? 0, 1);
  const safeRadius = Math.max(radius ?? 0, 0);
  const shadow = `rgba(0,0,0,${clamp(opacity ?? 0, 0, 1)})`;
  const transparentShadow = 'rgba(0,0,0,0)';
  const cornerSize = Math.max(safeWidth * 2, safeRadius * cornerScale);
  const bottomEdge = `linear-gradient(to top, ${shadow}, ${transparentShadow})`;
  const leftEdge = `linear-gradient(to right, ${shadow}, ${transparentShadow})`;
  const bottomLeftCorner = `radial-gradient(circle at 0 100%, ${shadow} 0%, ${transparentShadow} 70%)`;

  return {
    borderRadius: safeRadius,
    backgroundImage: [bottomEdge, leftEdge, bottomLeftCorner].join(', '),
    backgroundSize: [`100% ${safeWidth}px`, `${safeWidth}px 100%`, `${cornerSize}px ${cornerSize}px`].join(', '),
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'bottom center, center left, bottom left',
    backgroundOrigin: 'border-box',
    pointerEvents: 'none',
  };
}

function computeFlashProgress(current) {
  const min = TARGET_CARD_BRIGHTNESS;
  const max = TARGET_CARD_BRIGHTNESS * CARD_BRIGHTNESS_MULTIPLIER;
  if (max <= min) return 0;
  const normalized = (current - min) / (max - min);
  return clamp(normalized, 0, 1);
}

function FinalCardGlass({ flashProgress, children }) {
  const rimOpacity = clamp(0.18 + flashProgress * 0.12, 0.18, 0.35);
  const shadowStrength = 0.55 - flashProgress * 0.25;
  const ambientShadow = clamp(shadowStrength, 0.18, 0.55);
  const baseShadow = '0 32px 80px rgba(0,0,0,' + ambientShadow + ')';
  const highlightOpacity = clamp(0.48 + flashProgress * 0.3, 0.35, EDGE_HIGHLIGHT_MAX_OPACITY);
  const shadowOpacity = clamp(0.58 + flashProgress * 0.25, 0.4, EDGE_SHADOW_MAX_OPACITY);
  const cardHighlightStyle = createEdgeHighlightStyle({
    radius: CARD_RADIUS,
    width: FINAL_CARD_HIGHLIGHT_WIDTH,
    opacity: highlightOpacity,
    cornerScale: FINAL_EDGE_CORNER_SCALE,
  });
  const cardShadowStyle = createEdgeShadowStyle({
    radius: CARD_RADIUS,
    width: FINAL_CARD_SHADOW_WIDTH,
    opacity: shadowOpacity,
    cornerScale: FINAL_EDGE_CORNER_SCALE,
  });

  return (
    <div
      className="absolute inset-0"
      style={{
        borderRadius: CARD_RADIUS,
        overflow: 'hidden',
        backgroundColor: FINAL_CARD_BASE_COLOR,
        border: `1px solid rgba(255,255,255,${rimOpacity})`,
        boxShadow: baseShadow,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
      }}
    >
      {/* WebGL animated gradient background */}
      <div className="pointer-events-none absolute inset-0" style={{ borderRadius: CARD_RADIUS, overflow: 'hidden' }}>
        <WebGlGradientCanvas
          className="w-full h-full"
          style={{
            ['--gradient-color-1']: '#a960ee',
            ['--gradient-color-2']: '#ff333d',
            ['--gradient-color-3']: '#90e0ff',
            ['--gradient-color-4']: '#ffcb57',
          }}
        />
      </div>
      <span
        className="pointer-events-none absolute inset-0"
        style={{
          ...cardHighlightStyle,
          mixBlendMode: 'screen',
        }}
      />
      <span
        className="pointer-events-none absolute inset-0"
        style={{
          ...cardShadowStyle,
          mixBlendMode: 'multiply',
        }}
      />
      <span
        className="pointer-events-none absolute inset-0"
        style={{
          borderRadius: CARD_RADIUS,
          backgroundImage: FINAL_CARD_LIGHT_OVERLAY,
          mixBlendMode: 'screen',
          opacity: clamp(0.65 + flashProgress * 0.25, 0.4, 0.85),
        }}
      />
      <div className="relative z-10 flex h-full w-full flex-col items-center text-white" style={{ paddingTop: FINAL_CARD_CONTENT_TOP_OFFSET }}>
        {children}
      </div>
    </div>
  );
}

export default function FinalConversionSlide({
  title = '',
  currentStep,
  totalSteps,
  onNext,
  onBack,
  animationDirection = 'forward',
  isTransitioning = false,
  theme = 'dark',
  // Sizing controls: legacy iconSize applies to both; overrides available per state
  iconSize,
  lockedIconSize,
  unlockedIconSize=165,
  finalCardName = '',
  finalCardId = '',
  finalCardValidUntil = '',
  // preliminary colored card values (optional)
  candidateScorePercent = 72,
  expectedCostValue = 4300,
  expectedCostLabel = '$4200-$4300',
  expectedSatisfactionPercent = 92,
  medicalHairLossPercent = 85,
  medicalStressPercent = 65,
  medicalSocialImpactPercent = 55,
  aestheticGoalsPercent = 75,
}) {
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [cardScale, setCardScale] = useState(1);
  const contentAreaRef = useRef(null);
  // Brightness animation state for full-card flash on appear
  const [cardBrightness, setCardBrightness] = useState(TARGET_CARD_BRIGHTNESS);
  const brightnessAnimationRef = useRef(null);
  // Preliminary sequence state
  const [phase, setPhase] = useState('cards'); // 'cards' | 'merge' | 'flash' | 'final'
  const [preFlashProgress, setPreFlashProgress] = useState(0); // 0..1
  const cardRefs = useRef([]);
  const [initialTops, setInitialTops] = useState([]);
  const [mergeActivated, setMergeActivated] = useState(false);
  const [finalOverlayFade, setFinalOverlayFade] = useState(0);
  const [overlayAlpha, setOverlayAlpha] = useState(0); // white overlay alpha 0..1

  // Derived: colored cards for preliminary view
  const cards = useMemo(() => {
    const costPct = Math.max(0, Math.min(100, ((expectedCostValue - 0) / (10000 - 0)) * 100));
    return [
      { key: 'candidate', grad: 'bg-gradient-to-br from-indigo-400 to-sky-400', title: 'Hair Transplant Candidate Score', sub: 'How suitable are you for a successfull hair transplant', node: <ProgressBar percent={candidateScorePercent} label={`${candidateScorePercent}%`} minLabel="Low" maxLabel="High" color="bg-white" trackColor="bg-white/30" labelTextClass="text-sky-600" /> },
      { key: 'cost', grad: 'bg-gradient-to-br from-emerald-400 to-green-500', title: 'Expected Cost of Surgery', sub: 'Based on 1000+ cases matched to your attributes.', node: <ProgressBar percent={costPct} label={expectedCostLabel} minLabel="Low" maxLabel="High" color="bg-white" trackColor="bg-white/30" labelTextClass="text-emerald-600" /> },
      { key: 'satisfaction', grad: 'bg-gradient-to-br from-violet-500 to-fuchsia-500', title: 'Expected Satisfaction from Hair Transplantation', sub: 'Based on 1000+ cases matched to your attributes.', node: <ProgressBar percent={expectedSatisfactionPercent} label={`${expectedSatisfactionPercent}%`} minLabel="Low" maxLabel="High" color="bg-white" trackColor="bg-white/30" labelTextClass="text-fuchsia-600" /> },
      { key: 'medical', grad: 'bg-gradient-to-br from-teal-400 to-cyan-500', title: 'Medical Background', sub: '', node: (
          <DoubleProgressGroup
            bars={[
              { percent: medicalHairLossPercent, label: 'Hair loss level', color: 'bg-white', trackColor: 'bg-white/30', labelTextClass: 'text-cyan-700' },
              { percent: medicalStressPercent, label: 'Stress level', color: 'bg-white', trackColor: 'bg-white/30', labelTextClass: 'text-cyan-700' },
              { percent: medicalSocialImpactPercent, label: 'Social impact', color: 'bg-white', trackColor: 'bg-white/30', labelTextClass: 'text-cyan-700' },
            ]}
            minLabel="Low"
            maxLabel="High"
          />
        ) },
      { key: 'aesthetic', grad: 'bg-gradient-to-br from-rose-400 to-orange-400', title: 'Aesthetic Goals', sub: 'How realistic are your goals based on 1000+ cases matched to your attributes', node: <ProgressBar percent={aestheticGoalsPercent} label={`${aestheticGoalsPercent}%`} minLabel="Unrealistic" maxLabel="Realistic" color="bg-white" trackColor="bg-white/30" labelTextClass="text-rose-600" /> },
    ];
  }, [candidateScorePercent, expectedCostValue, expectedCostLabel, expectedSatisfactionPercent, medicalHairLossPercent, medicalStressPercent, medicalSocialImpactPercent, aestheticGoalsPercent]);

  // Scale to fit viewport similarly to ConversionSlide final state
  useEffect(() => {
    const calculateScale = () => {
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const headerHeight = 50;
      const footerHeight = 50;
      const titleHeight = 80;
      const verticalPadding = 80;

      const availableHeight = viewportHeight - headerHeight - footerHeight - titleHeight - verticalPadding;
      const availableWidth = viewportWidth - 32;

      if (availableHeight <= 0 || availableWidth <= 0) {
        setCardScale(1);
        return;
      }

      const scaleByHeight = availableHeight / CARD_H;
      const scaleByWidth = availableWidth / CARD_W;
      const targetScale = Math.min(scaleByHeight, scaleByWidth);
      // Clamp to reasonable bounds first
      let finalScale = Math.max(0.5, Math.min(1, targetScale));
      // If we have ample space (would render ~full size), shrink uniformly a bit for better aesthetics
      // so the card feels slightly more "premium" and less cramped. Avoid shrinking on already constrained viewports.
      if (finalScale > 0.8) {
        finalScale *= 0.9;
      }
      setCardScale(finalScale);
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);

  // Animate initial flash: start bright then fade to normal over time
  useEffect(() => {
    if (brightnessAnimationRef.current) {
      cancelAnimationFrame(brightnessAnimationRef.current);
      brightnessAnimationRef.current = null;
    }

    const startValue = TARGET_CARD_BRIGHTNESS * CARD_BRIGHTNESS_MULTIPLIER;
    const endValue = TARGET_CARD_BRIGHTNESS;
    const duration = CARD_BRIGHTNESS_FADE_DURATION_MS;
    let startTime;

    const animate = (timestamp) => {
      if (startTime === undefined) startTime = timestamp;
      const elapsed = timestamp - startTime;
      if (elapsed >= duration) {
        setCardBrightness(endValue);
        brightnessAnimationRef.current = null;
        return;
      }
      const progress = elapsed / duration;
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = startValue + (endValue - startValue) * eased;
      setCardBrightness(nextValue);
      brightnessAnimationRef.current = requestAnimationFrame(animate);
    };

    setCardBrightness(startValue);
    brightnessAnimationRef.current = requestAnimationFrame(animate);

    return () => {
      if (brightnessAnimationRef.current) {
        cancelAnimationFrame(brightnessAnimationRef.current);
        brightnessAnimationRef.current = null;
      }
    };
  }, []);

  // Preliminary sequence controller
  useEffect(() => {
    if (phase !== 'cards') return;
    const t = setTimeout(() => {
      // measure current stacked positions
      const tops = (cardRefs.current || []).map((el) => (el ? el.offsetTop : 0));
      setInitialTops(tops);
      setMergeActivated(false);
      setPhase('merge');
    }, PRE_DELAY_MS);
    return () => clearTimeout(t);
  }, [phase]);

  // Fade-in for colorful background overlay in final phase
  useEffect(() => {
    if (phase !== 'final') { setFinalOverlayFade(0); return; }
    const DUR = 2000;
    const start = performance.now();
    const run = (ts) => {
      const p = Math.min(1, (ts - start) / DUR);
      setFinalOverlayFade(p);
      if (p < 1) requestAnimationFrame(run);
    };
    setFinalOverlayFade(0);
    const raf = requestAnimationFrame(run);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  // Quick ramp-down of white overlay when final appears
  useEffect(() => {
    if (phase !== 'final') return;
    const startAlpha = overlayAlpha;
    const start = performance.now();
    const run = (ts) => {
      const p = Math.min(1, (ts - start) / FLASH_DECAY_MS);
      const a = Math.max(0, startAlpha * (1 - p));
      setOverlayAlpha(a);
      if (p < 1) requestAnimationFrame(run);
    };
    const raf = requestAnimationFrame(run);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'merge') return;
    // Start brightness ramp together with merge animation
    const start = performance.now();
    const run = (ts) => {
      const p = Math.min(1, (ts - start) / PRE_FLASH_MS);
      setPreFlashProgress(p);
      setOverlayAlpha(p);
      if (p < 1) requestAnimationFrame(run);
    };
    const raf = requestAnimationFrame(run);
    const toFinal = setTimeout(() => setPhase('final'), PRE_FLASH_MS);
    return () => { cancelAnimationFrame(raf); clearTimeout(toFinal); };
  }, [phase]);

  // Kick off merge transition (from measured top to center) on the next frame
  useEffect(() => {
    if (phase === 'merge') {
      const raf = requestAnimationFrame(() => setMergeActivated(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [phase]);

  return (
    <div className="relative min-h-screen" style={{ backgroundColor: '#1f1f1f' }}>
      {/* Global ambient glow via portal to avoid clipping and show behind title area */}
      {phase === 'final' && typeof document !== 'undefined'
        ? createPortal(
            (() => {
              const flash = computeFlashProgress(cardBrightness);
              const ambientOpacity = Math.min(0.35, 0.22 + flash * 0.12) * finalOverlayFade;
              return (
                <div
                  className="pointer-events-none fixed inset-0"
                  aria-hidden="true"
                  style={{ mixBlendMode: 'screen', opacity: ambientOpacity, filter: 'blur(48px)', zIndex: 15 }}
                >
                  <WebGlGradientCanvas
                    className="w-full h-full"
                    style={{
                      ['--gradient-color-1']: '#a960ee',
                      ['--gradient-color-2']: '#ff333d',
                      ['--gradient-color-3']: '#90e0ff',
                      ['--gradient-color-4']: '#ffcb57',
                    }}
                  />
                </div>
              );
            })(),
            document.body,
          )
        : null}
      {/* White radial growth overlay during brightness ramp and quick decay */}
      {overlayAlpha > 0 && (
        <div
          className="pointer-events-none fixed inset-0"
          aria-hidden="true"
          style={{
            zIndex: 30,
            mixBlendMode: 'screen',
            background: `radial-gradient(circle at 50% 50%, rgba(255,255,255,${0.95 * overlayAlpha}) 0%, rgba(255,255,255,${0.8 * overlayAlpha}) ${Math.min(100, 20 + preFlashProgress * 60)}%, rgba(255,255,255,0) ${Math.min(100, 35 + preFlashProgress * 90)}%)`,
            filter: 'blur(24px)',
            transition: 'background 50ms linear, opacity 80ms linear',
            opacity: overlayAlpha,
          }}
        />
      )}
      <SlideShell
  title={phase === 'final' ? title : ''}
        subtitle={''}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onNext={onNext}
  onBack={onBack}
        theme={theme}
        animationDirection={animationDirection}
        isTransitioning={isTransitioning}
        showFooterSubtext={false}
        titleClassName="bg-transparent onboarding-title-transparent text-white drop-shadow-[5px_5px_12px_rgba(0,0,0,1)]"
        titleContainerClassName="bg-transparent onboarding-title-transparent"
        titleContainerStyle={{
          background: 'transparent',
          backgroundColor: 'transparent',
          boxShadow: 'none',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
        }}
  backgroundClass="bg-transparent"
  backgroundStyle={{ background: 'transparent', backgroundColor: 'transparent' }}
        titleOverlay={
          <div className="absolute inset-0" style={{ mixBlendMode: 'screen', opacity: 0.5, filter: 'blur(64px)' }}>
            <WebGlGradientCanvas
              className="w-full h-full"
              style={{
                ['--gradient-color-1']: '#a960ee',
                ['--gradient-color-2']: '#ff333d',
                ['--gradient-color-3']: '#90e0ff',
                ['--gradient-color-4']: '#ffcb57',
              }}
            />
          </div>
        }
        showPrimary={phase === 'final' ? false : false}
        showBack={false}
      >
  <div ref={contentAreaRef} className="flex w-full h-full flex-col items-center justify-center px-4 py-10" style={{ filter: overlayAlpha > 0 ? `brightness(${1 + overlayAlpha * 2.0})` : undefined, transition: 'filter 80ms linear' }}>
          {phase !== 'final' && (
            <div className="relative w-full max-w-md" style={{ minHeight: 420 }}>
              {cards.map((c, idx) => {
                const merging = phase === 'merge' || phase === 'flash';
                if (!merging) {
                  return (
                    <div key={c.key} ref={(el) => (cardRefs.current[idx] = el)}>
                      <ColoredCard title={c.title} subtitle={c.sub} gradientClass={c.grad}>
                        {c.node}
                      </ColoredCard>
                    </div>
                  );
                }
                const startTop = initialTops[idx] ?? idx * 160;
                const centerTop = 120;
                const top = mergeActivated ? (centerTop + idx * 2) : startTop;
                const z = 10 + idx;
                return (
                  <ColoredCard
                    key={c.key}
                    title={c.title}
                    subtitle={c.sub}
                    gradientClass={c.grad}
                    className="absolute inset-x-0"
                    style={{ top, transition: `top ${MERGE_ANIM_MS}ms cubic-bezier(.16,1,.3,1)`, zIndex: z }}
                  >
                    {c.node}
                  </ColoredCard>
                );
              })}
            </div>
          )}
          {phase === 'final' && (
          <>
          <div
            className="relative"
            style={{
              width: CARD_W * cardScale,
              height: CARD_H * cardScale,
            }}
          >
            <div
              className="select-none"
              style={{
                width: CARD_W,
                height: CARD_H,
                transform: `scale(${cardScale})`,
                transformOrigin: 'top left',
                position: 'relative',
                filter: `brightness(${1 + computeFlashProgress(cardBrightness) * 0.6}) contrast(${1 + computeFlashProgress(cardBrightness) * 0.25})`,
                transition: 'filter 100ms linear',
              }}
            >
              {/* Animated color glow behind card: second gradient canvas enlarged + blurred */}
              {(() => {
                const flash = computeFlashProgress(cardBrightness);
                const glowOpacity = Math.min(0.6, 0.32 + flash * 0.28);
                const left = -GLOW_SPREAD;
                const top = -GLOW_SPREAD;
                const width = CARD_W + GLOW_SPREAD * 2;
                const height = CARD_H + GLOW_SPREAD * 2;
                return (
                  <div
                    className="pointer-events-none absolute z-0"
                    aria-hidden="true"
                    style={{
                      left,
                      top,
                      width,
                      height,
                      borderRadius: CARD_RADIUS * 1.2,
                      overflow: 'hidden',
                      filter: `blur(${GLOW_BLUR_PX}px) brightness(${1 + flash * 0.2})`,
                      opacity: glowOpacity,
                      mixBlendMode: 'screen',
                    }}
                  >
                    <WebGlGradientCanvas
                      className="w-full h-full"
                      style={{
                        ['--gradient-color-1']: '#a960ee',
                        ['--gradient-color-2']: '#ff333d',
                        ['--gradient-color-3']: '#90e0ff',
                        ['--gradient-color-4']: '#ffcb57',
                      }}
                    />
                  </div>
                );
              })()}
              <FinalCardGlass flashProgress={computeFlashProgress(cardBrightness)}>
                <div className="relative z-10 flex h-full w-full flex-col items-center text-white" style={{ gap: FINAL_CARD_CONTENT_GAP }}>
                  <MatchmedicLogo
                    className="text-white blur-[0.4px]"
                    style={{
                      width: FINAL_CARD_LOGO_WIDTH,
                      display: 'block',
                      filter: FINAL_CARD_LOGO_DROP_SHADOW,
                    }}
                    aria-label="Matchmedic"
                  />
                  <span
                    className="text-lg blur-[0.4px]"
                    style={{
                      lineHeight: 1,
                      marginTop: FINAL_CARD_LABEL_MARGIN_TOP,
                      textShadow: FINAL_CARD_LABEL_TEXT_SHADOW,
                    }}
                  >
                    patient file
                  </span>
                  {/* Centered locked icon overlay (visual size/config preserved) */}
                  {(() => {
                    const defaultSize = Math.max(8, Number(iconSize ?? 148));
                    const sizePx = Math.max(8, Number(unlockedIconSize ?? defaultSize));
                    const maskSrc = lockedIcon;
                    return (
                      <div
                        aria-label={'Locked'}
                        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-2/3 z-20 mt-0.5 flex flex-col items-center"
                        style={{ width: sizePx }}
                      >
                        <div
                          className="shadow-sm"
                          style={{
                            width: sizePx,
                            height: sizePx,
                            filter: 'blur(0.4px)',
                            backgroundColor: '#ffffff',
                            WebkitMaskImage: `url(${maskSrc})`,
                            WebkitMaskRepeat: 'no-repeat',
                            WebkitMaskPosition: 'center',
                            WebkitMaskSize: 'contain',
                            maskImage: `url(${maskSrc})`,
                            maskRepeat: 'no-repeat',
                            maskPosition: 'center',
                            maskSize: 'contain',
                          }}
                        />
                        <span
                          className="text-sm font-medium blur-[0.4px] text-center"
                          style={{
                            lineHeight: 1.2,
                            marginTop: 2,
                            padding: '0 6px',
                            maxWidth: sizePx,
                            textShadow: FINAL_CARD_LABEL_TEXT_SHADOW,
                            color: '#ffffff',
                          }}
                        >
                          Data secured
                          <br />
                          AES-256 Encryption
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </FinalCardGlass>
              <div className="absolute inset-x-0 bottom-0 z-10">
                <MetallicCard
                  className="w-full"
                  variant="steel"
                  id={finalCardId || ''}
                  name={finalCardName || ''}
                  validUntil={finalCardValidUntil || ''}
                />
              </div>
            </div>
          </div>
          <p
            className={`mt-2 title-fade text-sm leading-relaxed text-center ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}
            style={{ maxWidth: 720 }}
          >
            Your patient file is successfully created and will be used to effectively find and get quotes from the best clinics for you.
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className={`mt-1 title-fade text-sm leading-relaxed text-center underline ${
              isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current`}
            style={{ maxWidth: 720 }}
          >
            Return home
          </button>
          </>
          )}
        </div>
      </SlideShell>
    </div>
  );
}
